import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Loader2, Phone, Wallet, CreditCard, Clock, CheckCircle, AlertCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ParentPaymentDialog = ({ invoice, open, onClose, onPaymentSubmitted }) => {
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState('payment_method'); // 'payment_method', 'orange_money', 'cash', 'cheque', 'processing'
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [transactionId, setTransactionId] = useState(null);
  const [paymentId, setPaymentId] = useState(null);
  const [ussdCode, setUssdCode] = useState('');
  const [polling, setPolling] = useState(false);
  
  // Cash payment fields
  const [cashPaymentDate, setCashPaymentDate] = useState('');
  const [cashReceiptRef, setCashReceiptRef] = useState('');
  const [cashNotes, setCashNotes] = useState('');
  
  // Cheque payment fields
  const [chequeNumber, setChequeNumber] = useState('');
  const [bankName, setBankName] = useState('');
  const [chequeDate, setChequeDate] = useState('');
  const [chequeNotes, setChequeNotes] = useState('');

  useEffect(() => {
    if (open) {
      // Reset form when dialog opens
      setStep('payment_method');
      setPaymentMethod(null);
      setPhoneNumber('');
      setCashPaymentDate('');
      setCashReceiptRef('');
      setCashNotes('');
      setChequeNumber('');
      setBankName('');
      setChequeDate('');
      setChequeNotes('');
    }
  }, [open]);

  const handleOrangeMoneyPayment = async () => {
    if (!phoneNumber.startsWith('+224')) {
      toast.error('Format invalide. Utilisez +224XXXXXXXXX');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/payments/orange-money/initiate`,
        {
          invoice_id: invoice.id,
          customer_phone: phoneNumber
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setTransactionId(response.data.transaction_id);
      setUssdCode(response.data.ussd_code);
      setStep('processing');
      toast.success('Paiement initié! Composez le code USSD.');
      
      // Commencer le polling du statut
      startOrangeMoneyPolling(response.data.transaction_id);
    } catch (error) {
      console.error('Error initiating Orange Money payment:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'initiation du paiement');
    } finally {
      setLoading(false);
    }
  };

  const handleCashPayment = async () => {
    if (!cashPaymentDate || !cashReceiptRef) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/payments/cash/submit?invoice_id=${invoice.id}`,
        {
          amount: invoice.total_amount,
          payment_date: cashPaymentDate,
          receipt_reference: cashReceiptRef,
          notes: cashNotes
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setPaymentId(response.data.payment_id);
      setStep('processing');
      toast.success('Paiement enregistré! En attente de validation.');
      
      // Commencer le polling du statut
      startManualPaymentPolling(response.data.payment_id);
    } catch (error) {
      console.error('Error submitting cash payment:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'enregistrement du paiement');
    } finally {
      setLoading(false);
    }
  };

  const handleChequePayment = async () => {
    if (!chequeNumber || !bankName || !chequeDate) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/payments/cheque/submit?invoice_id=${invoice.id}`,
        {
          amount: invoice.total_amount,
          cheque_number: chequeNumber,
          bank_name: bankName,
          cheque_date: chequeDate,
          notes: chequeNotes
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setPaymentId(response.data.payment_id);
      setStep('processing');
      toast.success('Paiement enregistré! En attente de validation.');
      
      // Commencer le polling du statut
      startManualPaymentPolling(response.data.payment_id);
    } catch (error) {
      console.error('Error submitting cheque payment:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'enregistrement du paiement');
    } finally {
      setLoading(false);
    }
  };

  const startOrangeMoneyPolling = (txId) => {
    setPolling(true);
    const pollInterval = setInterval(async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(
          `${API}/payments/orange-money/status/${txId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (response.data.status === 'SUCCESS') {
          clearInterval(pollInterval);
          setPolling(false);
          toast.success('Paiement réussi!');
          setTimeout(() => {
            onPaymentSubmitted();
            onClose();
          }, 2000);
        } else if (response.data.status === 'FAILED' || response.data.status === 'EXPIRED') {
          clearInterval(pollInterval);
          setPolling(false);
          toast.error('Le paiement a échoué. Veuillez réessayer.');
          setStep('payment_method');
        }
      } catch (error) {
        console.error('Error polling Orange Money status:', error);
      }
    }, 3000); // Poll toutes les 3 secondes

    // Arrêter le polling après 5 minutes
    setTimeout(() => {
      clearInterval(pollInterval);
      setPolling(false);
    }, 300000);
  };

  const startManualPaymentPolling = (pId) => {
    setPolling(true);
    const pollInterval = setInterval(async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(
          `${API}/tenant/payment-status/${pId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (response.data.status === 'VALIDATED') {
          clearInterval(pollInterval);
          setPolling(false);
          toast.success('Paiement validé!');
          setTimeout(() => {
            onPaymentSubmitted();
            onClose();
          }, 2000);
        } else if (response.data.status === 'REJECTED') {
          clearInterval(pollInterval);
          setPolling(false);
          toast.error(`Paiement rejeté: ${response.data.rejection_reason}`);
          setStep('payment_method');
        }
      } catch (error) {
        console.error('Error polling payment status:', error);
      }
    }, 5000); // Poll toutes les 5 secondes

    // Arrêter le polling après 10 minutes
    setTimeout(() => {
      clearInterval(pollInterval);
      setPolling(false);
    }, 600000);
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-GN').format(price);
  };

  if (!invoice) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Payer la Facture</DialogTitle>
          <DialogDescription>
            Facture #{invoice.invoice_number} - {formatPrice(invoice.total_amount)} GNF
          </DialogDescription>
        </DialogHeader>

        {step === 'payment_method' && (
          <div className="space-y-6 py-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold text-lg">Montant à payer</p>
                  <p className="text-sm text-gray-600">{invoice.description}</p>
                </div>
                <div className="text-3xl font-bold text-blue-600">
                  {formatPrice(invoice.total_amount)} GNF
                </div>
              </div>
            </div>

            <div>
              <h3 className="font-semibold text-lg mb-4">Choisissez votre méthode de paiement</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Orange Money */}
                <Card 
                  className="border-2 hover:border-orange-500 cursor-pointer transition-colors"
                  onClick={() => {
                    setPaymentMethod('ORANGE_MONEY');
                    setStep('orange_money');
                  }}
                >
                  <CardContent className="pt-6 pb-6">
                    <div className="text-center space-y-3">
                      <Phone className="h-12 w-12 mx-auto text-orange-600" />
                      <h4 className="font-bold">Orange Money</h4>
                      <p className="text-xs text-gray-600">Paiement instantané</p>
                    </div>
                  </CardContent>
                </Card>

                {/* Cash - Désactivé */}
                <Card 
                  className="border-2 border-gray-200 bg-gray-50 opacity-60 cursor-not-allowed"
                  title="Méthode non disponible en ligne"
                >
                  <CardContent className="pt-6 pb-6">
                    <div className="text-center space-y-3">
                      <Wallet className="h-12 w-12 mx-auto text-gray-400" />
                      <h4 className="font-bold text-gray-500">Cash</h4>
                      <p className="text-xs text-gray-500">Non disponible en ligne</p>
                    </div>
                  </CardContent>
                </Card>

                {/* Chèque - Désactivé */}
                <Card 
                  className="border-2 border-gray-200 bg-gray-50 opacity-60 cursor-not-allowed"
                  title="Méthode non disponible en ligne"
                >
                  <CardContent className="pt-6 pb-6">
                    <div className="text-center space-y-3">
                      <CreditCard className="h-12 w-12 mx-auto text-gray-400" />
                      <h4 className="font-bold text-gray-500">Chèque</h4>
                      <p className="text-xs text-gray-500">Non disponible en ligne</p>
                    </div>
                  </CardContent>
                </Card>
              </div>
              
              {/* Message d'information pour Cash et Chèque */}
              <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-start space-x-3">
                  <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-blue-900">Paiements en espèces et chèques</p>
                    <p className="text-sm text-blue-800 mt-1">
                      Pour les paiements en espèces et chèques, merci de vous rendre à l'école ou de contacter le service financier.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {step === 'orange_money' && (
          <div className="space-y-6 py-4">
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold">Montant à payer</p>
                  <p className="text-sm text-gray-600">via Orange Money</p>
                </div>
                <div className="text-2xl font-bold text-orange-600">
                  {formatPrice(invoice.total_amount)} GNF
                </div>
              </div>
            </div>

            <div>
              <Label htmlFor="phone">Numéro Orange Money</Label>
              <Input
                id="phone"
                type="tel"
                placeholder="+224 6XX XX XX XX"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                className="mt-2"
              />
              <p className="text-xs text-gray-500 mt-1">
                Format: +224 6XX XX XX XX (ex: +224 621 23 45 67)
              </p>
            </div>

            <div className="flex justify-end space-x-3">
              <Button
                variant="outline"
                onClick={() => setStep('payment_method')}
                disabled={loading}
              >
                Retour
              </Button>
              <Button
                onClick={handleOrangeMoneyPayment}
                disabled={loading || !phoneNumber}
                className="bg-orange-600 hover:bg-orange-700"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Chargement...
                  </>
                ) : (
                  <>
                    <Phone className="h-4 w-4 mr-2" />
                    Payer
                  </>
                )}
              </Button>
            </div>
          </div>
        )}

        {step === 'cash' && (
          <div className="space-y-6 py-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold">Montant à payer</p>
                  <p className="text-sm text-gray-600">Paiement Cash</p>
                </div>
                <div className="text-2xl font-bold text-green-600">
                  {formatPrice(invoice.total_amount)} GNF
                </div>
              </div>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <p className="text-sm text-yellow-800">
                <strong>Note:</strong> Votre paiement sera vérifié par l'administration avant validation.
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <Label htmlFor="cash_date">Date de Paiement *</Label>
                <Input
                  id="cash_date"
                  type="date"
                  value={cashPaymentDate}
                  onChange={(e) => setCashPaymentDate(e.target.value)}
                  className="mt-2"
                />
              </div>

              <div>
                <Label htmlFor="cash_receipt">Référence du Reçu *</Label>
                <Input
                  id="cash_receipt"
                  type="text"
                  placeholder="Ex: REC-2024-001"
                  value={cashReceiptRef}
                  onChange={(e) => setCashReceiptRef(e.target.value)}
                  className="mt-2"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Numéro du reçu de paiement délivré
                </p>
              </div>

              <div>
                <Label htmlFor="cash_notes">Notes (optionnel)</Label>
                <Input
                  id="cash_notes"
                  type="text"
                  placeholder="Informations supplémentaires..."
                  value={cashNotes}
                  onChange={(e) => setCashNotes(e.target.value)}
                  className="mt-2"
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3">
              <Button
                variant="outline"
                onClick={() => setStep('payment_method')}
                disabled={loading}
              >
                Retour
              </Button>
              <Button
                onClick={handleCashPayment}
                disabled={loading || !cashPaymentDate || !cashReceiptRef}
                className="bg-green-600 hover:bg-green-700"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Enregistrement...
                  </>
                ) : (
                  <>
                    <Wallet className="h-4 w-4 mr-2" />
                    Enregistrer
                  </>
                )}
              </Button>
            </div>
          </div>
        )}

        {step === 'cheque' && (
          <div className="space-y-6 py-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold">Montant à payer</p>
                  <p className="text-sm text-gray-600">Paiement par Chèque</p>
                </div>
                <div className="text-2xl font-bold text-blue-600">
                  {formatPrice(invoice.total_amount)} GNF
                </div>
              </div>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <p className="text-sm text-yellow-800">
                <strong>Note:</strong> Votre paiement sera vérifié par l'administration avant validation.
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <Label htmlFor="cheque_number">Numéro du Chèque *</Label>
                <Input
                  id="cheque_number"
                  type="text"
                  placeholder="Ex: 1234567"
                  value={chequeNumber}
                  onChange={(e) => setChequeNumber(e.target.value)}
                  className="mt-2"
                />
              </div>

              <div>
                <Label htmlFor="bank_name">Nom de la Banque *</Label>
                <Input
                  id="bank_name"
                  type="text"
                  placeholder="Ex: BCRG, Ecobank, etc."
                  value={bankName}
                  onChange={(e) => setBankName(e.target.value)}
                  className="mt-2"
                />
              </div>

              <div>
                <Label htmlFor="cheque_date">Date du Chèque *</Label>
                <Input
                  id="cheque_date"
                  type="date"
                  value={chequeDate}
                  onChange={(e) => setChequeDate(e.target.value)}
                  className="mt-2"
                />
              </div>

              <div>
                <Label htmlFor="cheque_notes">Notes (optionnel)</Label>
                <Input
                  id="cheque_notes"
                  type="text"
                  placeholder="Informations supplémentaires..."
                  value={chequeNotes}
                  onChange={(e) => setChequeNotes(e.target.value)}
                  className="mt-2"
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3">
              <Button
                variant="outline"
                onClick={() => setStep('payment_method')}
                disabled={loading}
              >
                Retour
              </Button>
              <Button
                onClick={handleChequePayment}
                disabled={loading || !chequeNumber || !bankName || !chequeDate}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Enregistrement...
                  </>
                ) : (
                  <>
                    <CreditCard className="h-4 w-4 mr-2" />
                    Enregistrer
                  </>
                )}
              </Button>
            </div>
          </div>
        )}

        {step === 'processing' && (
          <div className="space-y-6 py-4">
            <div className="text-center space-y-4">
              {polling ? (
                <>
                  <Loader2 className="h-16 w-16 mx-auto animate-spin text-blue-600" />
                  <h3 className="text-xl font-semibold">
                    {paymentMethod === 'ORANGE_MONEY' ? 'Paiement en cours...' : 'En attente de validation...'}
                  </h3>
                  <p className="text-gray-600">
                    {paymentMethod === 'ORANGE_MONEY' 
                      ? 'Veuillez composer le code USSD sur votre téléphone pour confirmer le paiement.'
                      : 'Votre paiement est en cours de vérification par l\'administration. Vous serez notifié une fois validé.'}
                  </p>
                </>
              ) : (
                <>
                  <CheckCircle className="h-16 w-16 mx-auto text-green-600" />
                  <h3 className="text-xl font-semibold text-green-600">Paiement réussi!</h3>
                  <p className="text-gray-600">
                    Votre facture a été payée avec succès.
                  </p>
                </>
              )}
            </div>

            {/* Orange Money USSD Code */}
            {ussdCode && paymentMethod === 'ORANGE_MONEY' && (
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                <p className="text-sm font-semibold mb-2">Code USSD à composer:</p>
                <p className="text-2xl font-mono font-bold text-center text-orange-600">
                  {ussdCode}
                </p>
                <p className="text-xs text-gray-600 mt-2 text-center">
                  Composez ce code sur votre téléphone Orange Money pour confirmer
                </p>
              </div>
            )}

            {/* Manual Payment Waiting */}
            {(paymentMethod === 'CASH' || paymentMethod === 'CHEQUE') && polling && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-center space-x-2">
                  <Clock className="h-5 w-5 text-yellow-600" />
                  <p className="text-sm font-semibold text-yellow-800">En attente de validation</p>
                </div>
                <p className="text-xs text-gray-600 mt-2">
                  L'administration vérifiera votre paiement et validera votre facture dans les plus brefs délais.
                </p>
              </div>
            )}

            {/* Transaction/Payment ID */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-gray-700">
                <strong>{paymentMethod === 'ORANGE_MONEY' ? 'Transaction ID' : 'Paiement ID'}:</strong> {transactionId || paymentId}
              </p>
              {paymentMethod && (
                <p className="text-sm text-gray-700 mt-1">
                  <strong>Méthode:</strong> {
                    paymentMethod === 'ORANGE_MONEY' ? 'Orange Money' :
                    paymentMethod === 'CASH' ? 'Cash' : 'Chèque'
                  }
                </p>
              )}
            </div>

            {!polling && (
              <div className="flex justify-center">
                <Button onClick={onClose}>
                  Fermer
                </Button>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ParentPaymentDialog;
